<template>
  <div>
    <b-form @submit.prevent="submit">
      <b-row>
        <b-col cols="12">
          <b-form-group>
            <requiredComponent
              slot="label"
              :label="$lang.name_family"
              :rules="$v.form.parvandeh.$params"
            />
            <selectAsync
              :options="parvandehOptions"
              v-model="$v.form.parvandeh.$model"
              label="caption"
              id="id"
              :asyncFind="getParvandehCombo"
              :loading="parvandehLoading"
              :isValid="
                $v.form.parvandeh.$dirty ? !$v.form.parvandeh.$error : null
              "
              :disabled="mode === 'EDIT'"
            />
            <b-form-invalid-feedback
              :state="
                $v.form.parvandeh.$dirty ? !$v.form.parvandeh.$error : null
              "
            >
              {{ $lang.error_required | replace($lang.name_family) }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <TagComponent
            category_name="rule_type"
            :parentValue="form.ruleType"
            :label="$lang.rule_type"
            :parentPermissions="PERMISSIONS"
            :requiredLabel="ruleTypeIsRequired"
            :checkValidation="ruleTypeCheckValidation"
            :idTrack="true"
            @updateCheckValidation="ruleTypeUpdateCheckValidation"
            @getSelected="updateRuleType"
          />
        </b-col>
        <b-col cols="12">
          <b-form-group>
            <requiredComponent
              slot="label"
              :label="`${$lang.payment_base} (ریال)`"
              :rules="$v.form.paymentBase.$params"
            />
            <b-form-input
              v-model="amountByFormat"
              type="text"
              autocomplete="off"
              :state="
                $v.form.paymentBase.$dirty ? !$v.form.paymentBase.$error : null
              "
            />
            <b-form-invalid-feedback
              :state="
                $v.form.paymentBase.$dirty ? !$v.form.paymentBase.$error : null
              "
            >
              <div v-if="!$v.form.paymentBase.required">
                {{ $lang.error_required | replace($lang.payment_base) }}
              </div>
              <div v-if="!$v.form.paymentBase.numeric">
                {{ $lang.error_numeric | replace($lang.payment_base) }}
              </div>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <TagComponent
            category_name="order_unit"
            :parentValue="form.orderUnit"
            :label="$lang.unit"
            :parentPermissions="PERMISSIONS"
            :requiredLabel="orderUnitIsRequired"
            :checkValidation="orderUnitCheckValidation"
            :idTrack="true"
            @updateCheckValidation="orderUnitUpdateCheckValidation"
            @getSelected="updateOrderUnit"
          />
        </b-col>
        <b-col cols="12">
          <b-form-group>
            <requiredComponent
              slot="label"
              :label="$lang.rule_date"
              :rules="$v.form.ruleDate.$params"
            />
            <date-picker
              v-model="$v.form.ruleDate.$model"
              type="date"
              :state="$v.form.ruleDate.$dirty ? !$v.form.ruleDate.$error : null"
              inputReq="true"
              clearable
              simple
              format="YYYY-MM-DD"
              display-format="jYYYY/jMM/jDD"
              :class="
                $v.form.ruleDate.$dirty
                  ? $v.form.ruleDate.$error
                    ? 'invalid-date'
                    : 'valid-date'
                  : null
              "
            >
            </date-picker>
            <b-form-invalid-feedback
              :state="$v.form.ruleDate.$dirty ? !$v.form.ruleDate.$error : null"
            >
              <div v-if="!$v.form.ruleDate.required">
                {{ $lang.error_required | replace($lang.rule_date) }}
              </div>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label-cols="4">
            <requiredComponent
              slot="label"
              :label="$lang.default_rule"
              :rules="$v.form.defaultRule.$params"
            />
            <b-form-checkbox
              v-model="$v.form.defaultRule.$model"
              name="check-button"
              switch
              class="mt-2"
              :value="1"
              :unchecked-value="0"
            />
            <b-form-invalid-feedback
              :state="
                $v.form.defaultRule.$dirty ? !$v.form.defaultRule.$error : null
              "
            >
              <div v-if="!$v.form.defaultRule.required">
                {{ $lang.error_required | replace($lang.default_rule) }}
              </div>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12" v-if="mode === 'EDIT'">
          <b-form-group label-cols="4">
            <requiredComponent
              slot="label"
              :label="$lang.status"
              :rules="$v.form.isEnable.$params"
            />
            <b-form-checkbox
              v-model="$v.form.isEnable.$model"
              name="check-button"
              switch
              class="mt-2"
              :value="1"
              :unchecked-value="0"
            />
            <b-form-invalid-feedback
              :state="$v.form.isEnable.$dirty ? !$v.form.isEnable.$error : null"
            >
              <div v-if="!$v.form.isEnable.required">
                {{ $lang.error_required | replace($lang.status) }}
              </div>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12" class="text-right mt-3">
          <b-button
            type="reset"
            class="mr-2"
            variant="outline-danger"
            @click="close"
          >
            <mdicon name="close" size="24" />
            {{ $lang.close }}
          </b-button>
          <b-button
            variant="success"
            @click="submit"
            v-if="isAdmin(PERMISSIONS)"
          >
            <mdicon name="check" size="24" />
            {{ $lang.submitButton }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import TagComponent from "@/components/tag/tagComponent.vue";
import { required, numeric, requiredIf } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import {
  generateApiUrl,
  isAdmin,
  renderEumOptions,
  priceFormat,
  justDigit,
} from "@/shared/utils";

export default {
  inject: ["PERMISSIONS"],
  components: {
    datePicker: VuePersianDatetimePicker,
    TagComponent,
  },
  props: {
    mode: {
      type: String,
      default: "ADD",
    },
    item: {
      type: Object,
      default: null,
    },
  },
  validations: {
    form: {
      parvandeh: { required },
      ruleType: { required: requiredIf((vm) => vm.ruleTypeIsRequired) },
      paymentBase: { required, numeric },
      orderUnit: { required: requiredIf((vm) => vm.orderUnitIsRequired) },
      ruleDate: { required },
      defaultRule: {},
      isEnable: {},
    },
  },
  data() {
    return {
      parvandehLoading: false,
      parvandehOptions: [],
      ruleTypeIsRequired: true,
      ruleTypeCheckValidation: false,
      orderUnitIsRequired: true,
      orderUnitCheckValidation: false,
      form: {
        parvandeh: null,
        ruleType: null,
        paymentBase: null,
        orderUnit: null,
        ruleDate: null,
        defaultRule: 0,
        isEnable: 1,
      },
    };
  },
  computed: {
    amountByFormat: {
      get() {
        const amount = justDigit(this.form.paymentBase);
        return amount ? priceFormat(amount) : null;
      },
      set(val) {
        this.form.paymentBase = justDigit(val);
      },
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(value) {
        value && this.fetchData();
      },
    },
  },
  methods: {
    pathOr,
    generateApiUrl,
    isAdmin,
    renderEumOptions,
    priceFormat,
    justDigit,
    close() {
      this.$emit("close");
    },
    orderUnitUpdateCheckValidation(state) {
      this.orderUnitCheckValidation = state;
    },
    updateOrderUnit(value) {
      this.form.orderUnit = value;
    },
    ruleTypeUpdateCheckValidation(state) {
      this.ruleTypeCheckValidation = state;
    },
    updateRuleType(value) {
      this.form.ruleType = value;
    },
    async getParvandehCombo(query) {
      if (!query || query.length < 2) {
        return;
      }
      if (query.length > 2) {
        this.parvandehOptions = [];
      }

      try {
        this.parvandehLoading = true;
        const url = generateApiUrl(["cooperation", "parvandeh_combo"], [], {
          search_txt: query,
        });
        const response = await this.$httpService.get(url);
        const data = pathOr([], ["data", "data"], response);
        this.parvandehOptions = data.map((item) => {
          return this.generateParvandehItem(item);
        });
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.parvandehLoading = false;
      }
    },
    generateParvandehItem(item) {
      return {
        caption: `${pathOr("", ["parvandeh_number"], item)} - ${pathOr(
          "",
          ["person", "name"],
          item
        )} ${pathOr("", ["person", "family"], item)} - ${pathOr(
          "",
          ["term", "caption"],
          item
        )}`,
        id: pathOr("", ["parvandeh_code"], item),
      };
    },
    submit() {
      this.$v.$touch();
      this.ruleTypeCheckValidation = true;
      this.orderUnitCheckValidation = true;
      if (this.ruleTypeIsRequired && !this.form.ruleType) return;
      if (this.orderUnitIsRequired && !this.form.orderUnit) return;
      if (this.$v.$anyError) return;

      const payload = {
        parvandeh_code: pathOr(null, ["id"], this.form.parvandeh),
        rule_type_id: pathOr(null, ["id"], this.form.ruleType),
        payment_base: this.form.paymentBase,
        order_unit_id: pathOr(null, ["id"], this.form.orderUnit),
        rule_date: this.form.ruleDate,
        is_default: this.form.defaultRule,
        is_enable: this.form.isEnable,
      };
      this.$emit("submit", payload);
    },
    fetchData() {
      this.form.parvandeh = this.generateParvandehItem(
        pathOr(null, ["parvandeh"], this.item)
      );
      this.form.ruleType = pathOr(null, ["rule_type"], this.item);
      this.form.paymentBase = pathOr(null, ["payment_base"], this.item);
      this.form.orderUnit = pathOr(null, ["order_unit"], this.item);
      this.form.ruleDate = pathOr(null, ["rule_date", "gregorian"], this.item);
      this.form.defaultRule = pathOr(0, ["is_default"], this.item);
      this.form.isEnable = pathOr(0, ["is_enable"], this.item);
    },
  },
};
</script>
